import { ActiveStatus, ActiveStatuses } from './active-status';
import { Organization } from './organization';
import { Role, RoleModel } from './role';
import { User } from './user';
import { Person } from './person';
import { PrimaryStatus } from './primary-status';
import { UserModel } from './user.model';
import { ValidationError } from './validation.error';

export interface UserRole {
  id?: number;
  clazz?: string;
  code: string;
  role: Role;
  user?: User;
  group: Organization | Person;
  title?: string;
  degreeCode?: string;
  startDate?: Date;
  endDate?: Date;
  activeStatusCode: ActiveStatus;
  activeStatusDetail?: string;
  primaryStatus?: PrimaryStatus;
  targetMustBeInGroup?: Organization;
  compare?(other: UserRole): number;
}

export interface UserRoleEditable extends UserRole {
  userMustHaveRole?: Role;
  userMustBeInGroup?: Organization;
  targetMustHaveRole?: Role;
}

export interface StudentRole {
  id?: number;
  clazz?: string;
  code: string;
  role: Role;
  user?: Person;
  group: Organization | Person;
  degreeCode?: string;
  startTermCode?: { code: string; description: string };
  endTermCode?: { code: string; description: string };
  activeStatusCode: ActiveStatus;
  activeStatusDetail?: string;
}

export class UserRoleModel implements UserRoleEditable {
  id?: number;
  clazz?: string;
  code: string;
  role: Role;
  user?: User;
  group: Organization | Person;
  title?: string;
  degreeCode?: string;
  startDate?: Date;
  endDate?: Date;
  activeStatusCode: ActiveStatus;
  activeStatusDetail?: string;
  primaryStatus?: PrimaryStatus;
  userMustHaveRole?: Role;
  userMustBeInGroup?: Organization;
  targetMustHaveRole?: Role;
  targetMustBeInGroup?: Organization;

  static create(props: Partial<UserRoleEditable>): UserRole {
    if (!props || (!props.code && !props.role) || !props.group) {
      throw new ValidationError('Missing role type and/or group');
    }

    return new UserRoleModel(props);
  }

  constructor(props: Partial<UserRoleEditable>) {
    if (props.id) {
      this.id = props.id;
    }

    this.clazz = 'edu.ucdavis.gs.people.UserRole';
    this.code = props.code || (props.role && props.role.code) || '';

    const roleProps = props.role ? { ...props.role } : { code: props.code, name: props.code };
    this.role = RoleModel.create(roleProps) as Role;

    if (props.user) {
      this.user = UserModel.create(props.user) as User;
    }
    this.group = UserModel.create(props.group as Organization | Person) as Organization | Person;

    this.title = props.title;

    if (props.degreeCode) {
      this.degreeCode = props.degreeCode;
    }

    if (props.startDate) {
      this.startDate = new Date(props.startDate);
    }

    if (props.endDate) {
      this.endDate = new Date(props.endDate);
    }

    this.activeStatusCode = props.activeStatusCode || ActiveStatuses.ACTIVE;
    this.activeStatusDetail = props.activeStatusDetail;

    if (props.primaryStatus) {
      this.primaryStatus = props.primaryStatus;
    }

    if (props.userMustHaveRole) {
      this.userMustHaveRole = RoleModel.create({ ...props.userMustHaveRole }) as Role;
    }

    if (props.userMustBeInGroup) {
      this.userMustBeInGroup = UserModel.create(props.userMustBeInGroup) as Organization;
    }

    if (props.targetMustHaveRole) {
      this.targetMustHaveRole = RoleModel.create({ ...props.targetMustHaveRole }) as Role;
    }

    if (props.targetMustBeInGroup) {
      this.targetMustBeInGroup = UserModel.create(props.targetMustBeInGroup) as Organization;
    }
  }

  compare(other: UserRole): number {
    let compare = this.user && this.user.name ? this.user.name.localeCompare(other.user ? other.user.name : '') : 0;
    if (compare !== 0) {
      return compare;
    }

    compare = this.role.code.localeCompare(other.role && other.role.code);
    if (compare !== 0) {
      return compare;
    }

    compare = this.primaryStatus ? this.primaryStatus.localeCompare(other.primaryStatus as string) : 0;
    if (compare !== 0) {
      return compare;
    }

    compare = this.group && this.group.name ? this.group.name.localeCompare(other.group && other.group.name) : 0;
    if (compare !== 0) {
      return compare;
    }

    compare = this.activeStatusCode.localeCompare(other.activeStatusCode);
    if (compare !== 0) {
      return compare;
    }

    return (this.id || 0) - (other.id || 0);
  }
}



// WEBPACK FOOTER //
// ./src/gs-core/common/models/user-role.ts